import { createApp, h } from 'vue'
import { mapState } from 'pinia'
import { LINK_CLICKED_TYPE } from './constants'
import { isEmpty, isNullOrUndefined, objectHasKeys } from '@/utils/comparators'
import { userContextStore } from '@/store/userContext'
import { applicationStore } from '~/store/application/index.js'
import { useMainStore } from '@/store/index'

let mParticle

export default (nuxtApp) => {
  const component = createApp({
    setup () {
      const mainStore = useMainStore()
      const { $messages } = useNuxtApp()
      const initialTitle = $messages('meta.title')
      return {
        mainStore,
        $messages,
        initialTitle
      }
    },
    data () {
      return {
        EventType: JSON.parse(
          '{"Navigation":1,"Location":2,"Search":3,"Transaction":4,"UserContent":5,"UserPreference":6,"Social":7,"Other":8,"Unknown":0}'
        ),
        linkTypes: LINK_CLICKED_TYPE,
        formTypes: ['lead', 'signup', 'contact', 'support', 'newsletter'],
        isApplicationReady: false,
        isUserIdentified: false,
        queue: [],
        currentLocation: '',
        currentRoute: {},
        referrer: null,
        sessionId: null,
        mParticleConfig: {
          isDevelopmentMode: !nuxtApp.$config.public.isProduction,
          identifyRequest: {
            userIdentities: {
              other:
              nuxtApp.$cookies.get('google_clientid', { parseJSON: false }) ||
                null, // Set by acquisition team from GTM
              mobile_number: null,
              customerid: this.externalReferenceId || null,
              email: null
            }
          },
          identityCallback: () => {
            mParticle = window.mParticle
            this._userIdentityCallback()
            const gaMeasurementId = mParticle._getActiveForwarders().find(forwarder => forwarder.name === 'GoogleAnalytics4').settings.measurementId
            if (!gaMeasurementId) {
              this.event('gaMeasurementId missing for sessionId fetch', 8, {})
            }
            gtag('get', gaMeasurementId, 'session_id', (value) => {
              this.sessionId = value || null
              if (!this.sessionId) {
                this.event('sessionId returned null by ga is' + this.sessionId, 8, {})
              }
            })
          },
          dataPlan: {
            planId: 'web'
          }
        },

        // Page Data
        pageTitle: this.initialTitle,
        virtualPageUrl: ''
      }
    },
    computed: {
      ...mapState(userContextStore, ['externalReferenceId', 'locale', 'currency', 'userContext']),
      ...mapState(applicationStore, ['majorityApp']),
      /**
       * Events in the queue will be executed only if
       * application is ready and user is identified
       */
      isQueueOpened () {
        return (
          this.queue.length > 0 &&
          this.isApplicationReady &&
          this.isUserIdentified
        )
      },
      /**
       * Custom attributes which will be included in all eventts
       */
      customAttributes () {
        return {
          'Google.Location': (this.virtualPageUrl + (this.currentLocation.split('?')[1] ? '?' + this.currentLocation.split('?')[1] : '')) || 'null',
          'Google.Title': this.pageTitle || null,
          'TikTok.URL': this.currentLocation || 'null',
          'TikTok.Referrer': this.referrer || 'null',
          'TikTok.Callback': nuxtApp.$cookies.get('tiktok_ttclid', { parseJSON: false }) || 'null',
          'Facebook.BrowserId': nuxtApp.$cookies.get('_fbp', { parseJSON: false }) || 'null',
          'Facebook.ClickId': nuxtApp.$cookies.get('_fbc', { parseJSON: false }) || 'null',
          'Facebook.ActionSource': 'website'
        }
      },
      /**
       * Default attributes which will be included in all events
       */
      defaultAttributes () {
        return {
          locale: this.locale || null,
          full_page_url: this.currentLocation || null,
          virtual_page_url: this.virtualPageUrl || null,
          full_virtual_page_url: this.currentLocation || null,
          virtual_page_path: nuxtApp._route.path || null,
          page_title: this.pageTitle || null,
          session_id: this.sessionId || null,
          page_referrer: this.referrer
        }
      }
    },
    /**
     * Watchers attempts to execute the queue when any of following
     * properties has changed. `isQueueOpened` property coordinates
     * if queue should be executed or not.
     */
    watch: {
      isApplicationReady () {
        this._shift()
      },
      isUserIdentified () {
        this._shift()
      },
      /**
       * When a new item added to the queue this watcher shifts an item.
       * Since this action will also trigger this watcher, that will work
       * recursively.
       */
      queue () {
        this._shift()
      }
    },
    created () {
      this.referrer = document.referrer || null
      this.initializeKits()
    },
    methods: {
      initializeKits () {
        // configure the SDK
        window.mParticle = {
          config: this.mParticleConfig
        };

        // load the SDK
        (
          function (e) { window.mParticle = window.mParticle || {}; window.mParticle.EventType = { Unknown: 0, Navigation: 1, Location: 2, Search: 3, Transaction: 4, UserContent: 5, UserPreference: 6, Social: 7, Other: 8 }; window.mParticle.eCommerce = { Cart: {} }; window.mParticle.Identity = {}; window.mParticle.config = window.mParticle.config || {}; window.mParticle.config.rq = []; window.mParticle.config.snippetVersion = 2.3; window.mParticle.ready = function (e) { window.mParticle.config.rq.push(e) }; const i = ['upload', 'endSession', 'logError', 'logBaseEvent', 'logEvent', 'logForm', 'logLink', 'logPageView', 'setSessionAttribute', 'setAppName', 'setAppVersion', 'setOptOut', 'setPosition', 'startNewSession', 'startTrackingLocation', 'stopTrackingLocation']; const n = ['setCurrencyCode', 'logCheckout']; const t = ['identify', 'login', 'logout', 'modify']; i.forEach(function (e) { window.mParticle[e] = o(e) }); n.forEach(function (e) { window.mParticle.eCommerce[e] = o(e, 'eCommerce') }); t.forEach(function (e) { window.mParticle.Identity[e] = o(e, 'Identity') }); function o (i, n) { return function () { if (n) { i = n + '.' + i } const e = Array.prototype.slice.call(arguments); e.unshift(i); window.mParticle.config.rq.push(e) } } let r; let c; const a = window.mParticle.config; const s = a.isDevelopmentMode ? 1 : 0; let l = '?env=' + s; const w = window.mParticle.config.dataPlan; if (w) { r = w.planId; c = w.planVersion; if (r) { if (c && (c < 1 || c > 1e3)) { c = null }l += '&plan_id=' + r + (c ? '&plan_version=' + c : '') } } const d = window.mParticle.config.versions; const m = []; if (d) { Object.keys(d).forEach(function (e) { m.push(e + '=' + d[e]) }) } const f = document.createElement('script'); f.type = 'text/javascript'; f.defer = true; f.src = (document.location.protocol == 'https:' ? 'https://jssdkcdns' : 'http://jssdkcdn') + '.mparticle.com/js/v2/' + e + '/mparticle.js' + l + '&' + m.join('&'); const p = document.getElementsByTagName('script')[0]; p.parentNode.insertBefore(f, p) }
        )(nuxtApp.$config.public.mparticleKey)
        mParticle = window.mParticle
      },
      /**
       * Adds a custom event to queue
       * @param {String} eventName Events name that will appear on mParticle and other outputs
       * @param {Number} eventType mParticle event type (see `eventType` property)
       * @param {Object} attributes An object that holds custom attributes
       */
      event (eventName, eventType, extraAttributes) {
        this._push(this._event(eventName, eventType, extraAttributes))
      },

      // Trigger mParticle ID Sync “login”. Use the following code snippet and supply external reference id , mobile phone number and email. If email is missing, send “null” as value.
      loginUser (customerid, mobileNumber, email, isFirstSignIn, diaspora) {
        try {
          const identityRequest = {
            userIdentities: {
              customerid,
              mobile_number: mobileNumber
            }
          }

          const identityCallback = function (result) {
            // Copy attributes from previous user to current user
            result.getUser().setUserAttributes(result.getPreviousUser().getAllUserAttributes())

            const previousUser = result.getPreviousUser()

            // If the user is anonymous, create and send the alias request
            if (previousUser && Object.keys(previousUser.getUserIdentities().userIdentities).length === 0) {
              const aliasRequest = mParticle.Identity.createAliasRequest(result.getPreviousUser(), result.getUser())
              mParticle.Identity.aliasUsers(aliasRequest)
            }
          }

          mParticle.Identity.login(identityRequest, identityCallback)

          if (isFirstSignIn) {
            this._push({
              event: mParticle.logEvent(
                'sign_up',
                mParticle.EventType.Other,
                this.defaultAttributes,
                {
                  'GoogleAds.ECData': {
                    email,
                    phone_number: mobileNumber
                  },
                  ...this.customAttributes
                }
              )
            })
            mParticle.upload()
          }
          if (diaspora) {
            this.sendDiasporaEvent(diaspora, mobileNumber, email)
          }
        } catch (error) {
          console.error(error)
        }
      },

      sendDiasporaEvent (diaspora, mobileNumber, email) {
        const event = 'sign_up-' + diaspora.toLowerCase()
        this._push({
          event: mParticle.logEvent(
            event,
            mParticle.EventType.Other,
            this.defaultAttributes,
            {
              'GoogleAds.ECData': {
                email,
                phone_number: mobileNumber
              },
              ...this.customAttributes
            }
          )
        })
        mParticle.upload()
      },

      setUserEmail (email) {
        this._modifyUserIdentity('email', email)
      },
      /**
       * Adds or modifies user id to MP userIdendities as `customerid`
       * @param {String} userId Majority User ID
       */
      setUserId (userId) {
        this._modifyUserIdentity('customerid', userId)
      },
      /**
       * Identify mparticle user with phone number
       * @param {String} phoneNumber PhoneNumber
       */
      setUserPhoneNumber (phoneNumber) {
        this._identifyUser('mobile_number', phoneNumber)
      },
      /**
       * Returns a function that logs pageview to mParticle
       * This function should be pushed to queue
       * @param {Object} route Route object
       * @param {Object} attributes An object that holds custom attributes
       */
      _pageView () {
        return {
          event: mParticle.logPageView(
            'Viewed web page',
            this._mergeAttributes({}),
            this.customAttributes
          )
        }
      },
      /**
       * Returns a function that logs custom event to mParticle
       * This function should be pushed to queue
       * @param {String} eventName Events name that will appear on mParticle and other outputs
       * @param {Number} eventType mParticle event type (see `eventType` property)
       * @param {Object} attributes An object that holds custom attributes
       */
      _event (eventName, eventType, attributes) {
        return {
          event: mParticle.logEvent(
            eventName,
            eventType,
            this._mergeAttributes(attributes),
            this.customAttributes
          )
        }
      },
      /**
       * Stores route in a component's data property to be used for events
       * @param {String} location current URL
       * @param {Object} route Current route object
       */
      _setCurrentLocation (location, route) {
        this.currentLocation = location
        this.virtualPageUrl = this.currentLocation.split('?')[0]
        this.currentRoute = route
      },

      /**
       * Pushes a function to queue
       * @param {Function} func A function that logs an event on mParticle
       */
      _push (func) {
        this.queue.push(func)
      },
      /**
       * Shifts a function from the queue and executes
       */
      _shift () {
        if (this.isQueueOpened && this.queue[0].event) {
          this.queue.shift().call()
        }
      },
      /**
       * Callback method when user identify is completed.
       * Sets user attributes and flags.
       * Assigned in mParticle configuration.
       */
      _userIdentityCallback () {
        const currentUser = mParticle.Identity.getCurrentUser()
        this._setUserContextToAttributes(currentUser)
        this._setAcquisitionCookiesToAttributes(currentUser)
        this.isUserIdentified = true
      },
      _setCustomUserAttributes (user) {
        user.setUserAttribute('page_referrer', this.referrer)
      },
      /**
       * Sets user context information as user attributes
       * on mParticle
       * @param {Object} user current mParticle user
       */
      _setUserContextToAttributes (user) {
        const contextKeys = Object.keys(this.userContext)
        contextKeys.forEach((key) => {
          if (
            !isEmpty(this.userContext[key]) &&
            typeof this.userContext[key] === 'string'
          ) {
            user.setUserAttribute(key, this.userContext[key])
          }
        })
      },
      /**
       * Reads acquisition cookies and  Sets them as user attributes
       * on mParticle
       * @param {Object} user current mParticle user
       */
      _setAcquisitionCookiesToAttributes (user) {
        const cookies = [
          'channel',
          'utm_campaign',
          'utm_source',
          'utm_medium',
          'utm_content',
          'utm_term',
          'google_gclid',
          'microsoft_msclkid',
          '_fbp',
          '_fbc',
          'tiktok_ttclid'
        ]
        cookies.forEach((cookieKey) => {
          // if parseJSON is true parse integers as well which causes character loss
          const cookie = nuxtApp.$cookies.get(cookieKey, { parseJSON: false })
          const attrName = cookieKey === '_fbp' ? 'meta_fbp' : cookieKey === '_fbc' ? 'meta_fbc' : cookieKey
          user.setUserAttribute(attrName, cookie || '')
        })
      },
      /**
       * Modifies user identity
       * @param {String} identity MP Identity Key
       * @param {String} value Identity field value
       * @param {Function} callback Callback function
       */
      _modifyUserIdentity (identity, value, callback = null) {
        this.isUserIdentified = false
        mParticle.Identity.modify(
          {
            userIdentities: this._getAllIdentities(identity, value)
          },
          () => {
            this.isUserIdentified = true
            if (isNullOrUndefined(callback)) {
              return
            }
            callback()
          }
        )
      },
      /**
       * Identify User
       * @param {String} identity MP Identity Key
       * @param {String} value Identity field value
       * @param {Function} callback Callback function
       */
      _identifyUser (identity, value, callback = null) {
        this.isUserIdentified = false

        mParticle.Identity.identify(
          {
            userIdentities: this._getAllIdentities(identity, value)
          },
          (result) => {
            this.isUserIdentified = true

            result
              .getUser()
              .setUserAttributes(
                result.getPreviousUser().getAllUserAttributes()
              )
            mParticle.Identity.aliasUsers(
              mParticle.Identity.createAliasRequest(
                result.getPreviousUser(),
                result.getUser()
              )
            )

            if (isNullOrUndefined(callback)) {
              return
            }
            callback()
          }
        )
      },
      _getAllIdentities (identity, value) {
        const identities = { [identity]: value }

        if (
          !identities.other &&
          nuxtApp.$cookies.get('google_clientid', { parseJSON: false })
        ) {
          identities.other = nuxtApp.$cookies.get('google_clientid', {
            parseJSON: false
          })
        }
        return identities
      },
      /**
       * Takes custom attributes and returns a new object merged with default attributes
       * @param {Object} extraAttributes custom attributes
       */
      _mergeAttributes (extraAttributes) {
        const attributes = objectHasKeys(extraAttributes)
          ? { ...this.defaultAttributes, ...extraAttributes }
          : { ...this.defaultAttributes }
        for (const key in attributes) {
          if (attributes[key] === undefined) {
            attributes[key] = null
          }
        }
        return attributes
      },
      /****************
       ** NEW EVENTS **
       ****************/

      pageView (route) {
        if (window && !window.dataLayer) {
          window.dataLayer = []
        }
        if (!this.sessionId) {
          this.event('sessionId missing in pageview', 8, {})
        }
        this.pageTitle = document.title || null
        this._setCurrentLocation(window.location.href, route)
        this._push(this._pageView())
        mParticle.upload()
        this.referrer = this.mainStore.referrer || null
      },
      /**
       *
       * @param {string} linkName
       * @param {linkType} linkType
       * @param {object} optionalAttributes
       * @param {string} optionalAttributes.linkText
       * @param {string} optionalAttributes.linkUrl
       * @param {string} optionalAttributes.linkRouteKey
       * @param {string} optionalAttributes.linkTitle
       * @param {string} optionalAttributes.linkImgSrc
       * @param {string} optionalAttributes.linkAppStoreName
       * @param {object} extraAttributes
       */
      linkClicked (
        linkName,
        linkType,
        {
          linkText,
          linkUrl,
          linkRouteKey,
          linkTitle,
          linkImgSrc,
          linkAppStoreName
        } = {},
        extraAttributes
      ) {
        if (!this.linkTypes.includes(linkType)) {
          this._validationError(`${linkType} is not a valid link type`)
          return
        }
        let attr = {
          linkName,
          linkType,
          linkRouteKey,
          linkTitle,
          linkText,
          linkUrl,
          linkImgSrc,
          linkAppStoreName
        }
        if (objectHasKeys(extraAttributes)) {
          attr = { ...attr, ...extraAttributes }
        }

        // this._push(this._event('linkClicked', 1, attr))
      },

      /**
       *
       * @param {string} formName
       * @param {string} formType
       * @param {object} optionalAttributes
       * @param {string} optionalAttributes.formTitle
       * @param {string} optionalAttributes.formField
       * @param {object} extraAttributes
       */
      formInputFocused (
        formName,
        formType,
        { formTitle, formField } = {},
        extraAttributes
      ) {
        if (!this.formTypes.includes(formType)) {
          this._validationError(`${formType} is not a valid form type`)
          return
        }
        let attr = {
          formName,
          formType,
          formTitle,
          formField
        }
        if (objectHasKeys(extraAttributes)) {
          attr = { ...attr, ...extraAttributes }
        }

        // this._push(this._event('formInputFocused', 5, attr))
      },

      /**
       *
       * @param {string} formName
       * @param {string} formType
       * @param {object} optionalAttributes
       * @param {string} optionalAttributes.formTitle
       * @param {object} extraAttributes
       */
      formSubmitted (formName, formType, { formTitle } = {}, extraAttributes) {
        if (!this.formTypes.includes(formType)) {
          this._validationError(`${formType} is not a valid form type`)
          return
        }

        let attr = {
          formName,
          formType,
          formTitle
        }
        if (objectHasKeys(extraAttributes)) {
          attr = { ...attr, ...extraAttributes }
        }

        // this._push(this._event('formButtonClicked', 5, attr))
      },

      /**
       *
       * @param {string} formName
       * @param {string} formType
       * @param {object} optionalAttributes
       * @param {string} optionalAttributes.formTitle
       * @param {string} optionalAttributes.formStatus
       * @param {string} optionalAttributes.formLeadStatus
       * @param {object} extraAttributes
       */
      formCompleted (
        formName,
        formType,
        { formTitle, formStatus, formLeadStatus } = {},
        extraAttributes
      ) {
        if (!this.formTypes.includes(formType)) {
          this._validationError(`${formType} is not a valid form type`)
          return
        }

        let attr = {
          formName,
          formType,
          formTitle,
          formStatus,
          formLeadStatus
        }
        if (objectHasKeys(extraAttributes)) {
          attr = { ...attr, ...extraAttributes }
        }

        // this._push(this._event('formCompleted', 5, attr))
      },

      /**
       *
       * @param {string} searchName
       * @param {object} optionalAttributes
       * @param {string} optionalAttributes.searchTitle
       * @param {object} extraAttributes
       */
      searchInputFocused (searchName, { searchTitle } = {}, extraAttributes) {
        let attr = {
          searchName,
          searchTitle
        }
        if (objectHasKeys(extraAttributes)) {
          attr = { ...attr, ...extraAttributes }
        }

        // this._push(this._event('searchInputFocused', 3, attr))
      },

      /**
       *
       * @param {string} searchName
       * @param {boolean} searchHasResult
       * @param {object} optionalAttributes
       * @param {string} optionalAttributes.searchTitle
       * @param {string} optionalAttributes.searchQuery
       * @param {object} extraAttributes
       */
      viewSearchResults (
        searchName,
        searchHasResult,
        { searchTitle, searchQuery, searchType } = {},
        extraAttributes
      ) {
        let attr = {
          searchName,
          searchHasResult,
          searchTitle,
          searchQuery,
          searchType
        }
        if (objectHasKeys(extraAttributes)) {
          attr = { ...attr, ...extraAttributes }
        }

        // this._push(this._event('viewSearchResults', 3, attr))
      },

      /**
       *
       * @param {string} searchName
       * @param {string} searchSelection
       * @param {object} optionalAttributes
       * @param {string} optionalAttributes.searchTitle
       * @param {string} optionalAttributes.searchQuery
       * @param {object} extraAttributes
       */
      searchResultSelected (
        searchName,
        searchSelection,
        { searchTitle, searchQuery } = {},
        extraAttributes
      ) {
        let attr = {
          searchName,
          searchSelection,
          searchTitle,
          searchQuery
        }
        if (objectHasKeys(extraAttributes)) {
          attr = { ...attr, ...extraAttributes }
        }

        // this._push(this._event('searchResultSelected', 3, attr))
      },

      _validationError (message = 'Tracking validation error') {
        const exception = new Error(message)
        if (nuxtApp.$config.public.isLocal) {
          throw exception
        } else {
          nuxtApp.$sentry.captureException(exception)
        }
      }
    },
    render: () => h('span', { id: 'mPartNone' })
  })

  return component
}
